import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, Trash2, Clock, Users, ChevronDown, ChevronRight, KeyRound } from 'lucide-react';
import * as adminApi from '../../api/admin';
import { generateTempPassword } from '../../utils/password';
import C from '../../constants/colors';

const STATUS_COLOR = {
  TRIAL:   C.blue,
  ACTIVE:  C.green,
  HALTED:  C.amber,
  PURGED:  C.textMuted,
};

function StatusBadge({ status }) {
  const color = STATUS_COLOR[status] || C.textMuted;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '20px',
      fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em',
      background: `${color}22`, color, border: `1px solid ${color}44`,
    }}>
      {status}
    </span>
  );
}

function ConfirmDialog({ title, message, confirmLabel, confirmColor, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: '16px', padding: '28px', maxWidth: '400px', width: '90%',
      }}>
        <h3 style={{ color: C.textPrimary, fontSize: '16px', fontWeight: 600, margin: '0 0 10px' }}>{title}</h3>
        <p style={{ color: C.textSecondary, fontSize: '13px', lineHeight: 1.6, margin: '0 0 24px' }}>{message}</p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{
            padding: '8px 16px', borderRadius: '8px', border: `1px solid ${C.border}`,
            background: 'transparent', color: C.textSecondary, fontSize: '13px', fontWeight: 500, cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: confirmColor || C.red, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
          }}>{confirmLabel || 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordDialog({ user, onClose }) {
  const [password, setPassword] = useState(() => generateTempPassword());
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState('');
  const [done,     setDone]     = useState(false);
  const [copied,   setCopied]   = useState(false);

  async function save() {
    if (password.length < 8) { setErr('Password must be at least 8 characters'); return; }
    setSaving(true);
    setErr('');
    try {
      await adminApi.resetUserPassword(user.id, password);
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to reset password.');
    } finally {
      setSaving(false);
    }
  }

  function copy() {
    navigator.clipboard?.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: '16px', padding: '28px', maxWidth: '440px', width: '90%',
      }}>
        <h3 style={{ color: C.textPrimary, fontSize: '16px', fontWeight: 600, margin: '0 0 10px' }}>
          {done ? 'Password reset' : 'Reset password'}
        </h3>

        {done ? (
          <>
            <p style={{ color: C.textSecondary, fontSize: '13px', lineHeight: 1.6, margin: '0 0 8px' }}>
              The password for <strong style={{ color: C.textPrimary }}>{user.email}</strong> has been reset.
              Copy it now — it cannot be shown again.
            </p>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 24px',
              padding: '10px 12px', borderRadius: '8px',
              background: C.surfaceElevated, border: `1px solid ${C.border}`,
            }}>
              <code style={{ flex: 1, color: C.green, fontSize: '14px', fontWeight: 600, letterSpacing: '0.04em' }}>{password}</code>
              <button onClick={copy} style={{
                padding: '5px 10px', borderRadius: '6px', border: `1px solid ${C.border}`,
                background: 'transparent', color: copied ? C.green : C.textSecondary,
                fontSize: '11px', fontWeight: 600, cursor: 'pointer',
              }}>{copied ? 'Copied' : 'Copy'}</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{
                padding: '8px 16px', borderRadius: '8px', border: 'none',
                background: C.blue, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              }}>Done</button>
            </div>
          </>
        ) : (
          <>
            <p style={{ color: C.textSecondary, fontSize: '13px', lineHeight: 1.6, margin: '0 0 16px' }}>
              Set a new password for <strong style={{ color: C.textPrimary }}>{user.name}</strong> ({user.email}).
              A strong one has been generated — edit it if you need to.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: err ? '8px' : '24px' }}>
              <input
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
                style={{
                  flex: 1, padding: '9px 12px', borderRadius: '8px',
                  border: `1px solid ${C.border}`, background: C.surfaceElevated,
                  color: C.textPrimary, fontSize: '14px', fontFamily: 'monospace', letterSpacing: '0.04em',
                }}
              />
              <button onClick={() => setPassword(generateTempPassword())} title="Generate a new one" style={{
                padding: '9px 12px', borderRadius: '8px', border: `1px solid ${C.border}`,
                background: 'transparent', color: C.textSecondary, cursor: 'pointer',
                display: 'flex', alignItems: 'center',
              }}><RefreshCw size={13} /></button>
            </div>
            {err && <div style={{ color: C.red, fontSize: '12px', margin: '0 0 16px' }}>{err}</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={onClose} disabled={saving} style={{
                padding: '8px 16px', borderRadius: '8px', border: `1px solid ${C.border}`,
                background: 'transparent', color: C.textSecondary, fontSize: '13px', fontWeight: 500, cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={save} disabled={saving} style={{
                padding: '8px 16px', borderRadius: '8px', border: 'none',
                background: C.blue, color: '#fff', fontSize: '13px', fontWeight: 600,
                cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1,
              }}>{saving ? 'Resetting…' : 'Reset Password'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysLeft(trialEndsAt) {
  if (!trialEndsAt) return null;
  return Math.ceil((new Date(trialEndsAt) - Date.now()) / 86400000);
}

export default function SuperAdminView() {
  const [tenants,    setTenants]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [actionErr,  setActionErr]  = useState('');
  const [confirm,    setConfirm]    = useState(null);
  const [expanded,   setExpanded]   = useState({});   // tenantId → boolean
  const [resetUser,  setResetUser]  = useState(null); // user being password-reset

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.listTenants();
      setTenants(data);
    } catch {
      setError('Failed to load tenants.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function toggleExpand(id) {
    setExpanded(p => ({ ...p, [id]: !p[id] }));
  }

  async function extendTrial(tenant) {
    setActionErr('');
    try {
      const current = tenant.trialDays || 30;
      const updated = await adminApi.updateTenant(tenant.id, { trialDays: current + 30 });
      setTenants(p => p.map(t => t.id === updated.id ? { ...t, ...updated } : t));
    } catch {
      setActionErr(`Failed to extend trial for ${tenant.name}.`);
    }
  }

  async function expireTenant(tenant) {
    setActionErr('');
    try {
      const updated = await adminApi.updateTenant(tenant.id, { status: 'HALTED' });
      setTenants(p => p.map(t => t.id === updated.id ? { ...t, ...updated } : t));
    } catch {
      setActionErr(`Failed to expire ${tenant.name}.`);
    }
    setConfirm(null);
  }

  async function purgeTenant(tenant) {
    setActionErr('');
    try {
      await adminApi.purgeTenant(tenant.id);
      setTenants(p => p.map(t => t.id === tenant.id
        ? { ...t, status: 'PURGED', name: `[PURGED] ${t.name}` } : t));
    } catch (err) {
      setActionErr(err.response?.data?.error || `Failed to purge ${tenant.name}.`);
    }
    setConfirm(null);
  }

  const cols = '24px 2.2fr 1.8fr 90px 120px 55px 180px';

  return (
    <div style={{ padding: '28px 32px', overflowY: 'auto', height: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ color: C.textPrimary, fontSize: '22px', fontWeight: 700, margin: 0 }}>Platform Admin</h1>
          <p style={{ color: C.textMuted, fontSize: '13px', marginTop: '4px' }}>
            Manage demo tenants — extend trials, expire, or purge accounts
          </p>
        </div>
        <button onClick={load} style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '8px 14px', borderRadius: '8px', border: `1px solid ${C.border}`,
          background: 'transparent', color: C.textSecondary, fontSize: '13px', fontWeight: 500, cursor: 'pointer',
        }}>
          <RefreshCw size={13} />Refresh
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {['TRIAL', 'ACTIVE', 'HALTED', 'PURGED'].map(s => {
          const count = tenants.filter(t => t.status === s).length;
          const color = STATUS_COLOR[s];
          return (
            <div key={s} style={{
              padding: '12px 20px', borderRadius: '10px',
              background: `${color}12`, border: `1px solid ${color}30`, minWidth: '90px',
            }}>
              <div style={{ color, fontSize: '22px', fontWeight: 700 }}>{count}</div>
              <div style={{ color: C.textMuted, fontSize: '11px', fontWeight: 600, marginTop: '2px' }}>{s}</div>
            </div>
          );
        })}
      </div>

      {actionErr && (
        <div style={{
          background: 'rgba(239,68,68,0.1)', border: `1px solid rgba(239,68,68,0.3)`,
          borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <AlertTriangle size={14} color={C.red} />
          <span style={{ color: C.red, fontSize: '13px' }}>{actionErr}</span>
        </div>
      )}

      {loading ? (
        <div style={{ color: C.textMuted, fontSize: '14px', paddingTop: '40px', textAlign: 'center' }}>Loading…</div>
      ) : error ? (
        <div style={{ color: C.red, fontSize: '14px', paddingTop: '40px', textAlign: 'center' }}>{error}</div>
      ) : tenants.length === 0 ? (
        <div style={{ color: C.textMuted, fontSize: '14px', paddingTop: '40px', textAlign: 'center' }}>No tenants found.</div>
      ) : (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>

          {/* Header row */}
          <div style={{
            display: 'grid', gridTemplateColumns: cols,
            padding: '10px 16px', borderBottom: `1px solid ${C.border}`,
            fontSize: '11px', fontWeight: 600, color: C.textMuted,
            textTransform: 'uppercase', letterSpacing: '0.05em', alignItems: 'center',
          }}>
            <span />
            <span>Organisation</span>
            <span>Admin Email</span>
            <span>Status</span>
            <span>Trial Ends</span>
            <span>Users</span>
            <span style={{ textAlign: 'right' }}>Actions</span>
          </div>

          {tenants.map((t, i) => {
            const days      = t.status === 'TRIAL' ? daysLeft(t.trialEndsAt) : null;
            const isPurged  = t.status === 'PURGED';
            const isOpen    = expanded[t.id];
            const userList  = t.users || [];

            return (
              <React.Fragment key={t.id}>
                {/* Main row */}
                <div style={{
                  display: 'grid', gridTemplateColumns: cols,
                  padding: '12px 16px', alignItems: 'center',
                  borderBottom: `1px solid ${C.border}`,
                  background: isPurged ? 'rgba(255,255,255,0.01)' : 'transparent',
                  opacity: isPurged ? 0.5 : 1,
                }}>
                  {/* Expand toggle */}
                  <button onClick={() => toggleExpand(t.id)} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: C.textMuted, display: 'flex', alignItems: 'center', padding: 0,
                  }}>
                    {isOpen
                      ? <ChevronDown size={13} />
                      : <ChevronRight size={13} />}
                  </button>

                  {/* Org */}
                  <div>
                    <div style={{ color: C.textPrimary, fontSize: '13px', fontWeight: 600 }}>{t.name}</div>
                    <div style={{ color: C.textMuted, fontSize: '11px', marginTop: '2px' }}>
                      Registered {formatDate(t.createdAt)}
                    </div>
                  </div>

                  {/* Admin email */}
                  <div style={{ color: C.textSecondary, fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.adminEmail}
                  </div>

                  {/* Status */}
                  <div><StatusBadge status={t.status} /></div>

                  {/* Trial ends */}
                  <div>
                    <div style={{ color: C.textSecondary, fontSize: '12px' }}>{formatDate(t.trialEndsAt)}</div>
                    {days !== null && (
                      <div style={{
                        fontSize: '11px', marginTop: '2px',
                        color: days <= 3 ? C.red : days <= 7 ? C.amber : C.textMuted,
                      }}>
                        {days > 0 ? `${days}d left` : 'Expired'}
                      </div>
                    )}
                  </div>

                  {/* User count */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: C.textSecondary, fontSize: '12px' }}>
                    <Users size={11} />{t.userCount ?? 0}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                    {(t.status === 'TRIAL' || t.status === 'HALTED') && (
                      <button onClick={() => extendTrial(t)} title="Extend trial by 30 days" style={{
                        display: 'flex', alignItems: 'center', gap: '4px',
                        padding: '5px 10px', borderRadius: '6px',
                        border: `1px solid ${C.blue}44`, background: `${C.blue}18`,
                        color: C.blue, fontSize: '11px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                      }}>
                        <Clock size={10} />+30d
                      </button>
                    )}
                    {t.status === 'TRIAL' && (
                      <button onClick={() => setConfirm({ type: 'expire', tenant: t })} title="Expire trial now" style={{
                        display: 'flex', alignItems: 'center', gap: '4px',
                        padding: '5px 10px', borderRadius: '6px',
                        border: `1px solid ${C.amber}44`, background: `${C.amber}18`,
                        color: C.amber, fontSize: '11px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                      }}>
                        <ChevronDown size={10} />Expire
                      </button>
                    )}
                    {t.status === 'HALTED' && (
                      <button onClick={() => setConfirm({ type: 'purge', tenant: t })} title="Permanently purge" style={{
                        display: 'flex', alignItems: 'center', gap: '4px',
                        padding: '5px 10px', borderRadius: '6px',
                        border: `1px solid ${C.red}44`, background: `${C.red}18`,
                        color: C.red, fontSize: '11px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                      }}>
                        <Trash2 size={10} />Purge
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded user list */}
                {isOpen && (
                  <div style={{
                    borderBottom: i < tenants.length - 1 ? `1px solid ${C.border}` : 'none',
                    background: 'rgba(255,255,255,0.02)',
                    padding: '10px 16px 14px 40px',
                  }}>
                    <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                      Team members ({userList.length} / 5)
                    </div>
                    {userList.length === 0 ? (
                      <div style={{ color: C.textMuted, fontSize: '12px' }}>No users found.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {userList.map((u, ui) => (
                          <div key={u.id || ui} style={{
                            display: 'flex', alignItems: 'center', gap: '16px',
                            padding: '7px 12px', borderRadius: '8px',
                            background: C.surfaceElevated, border: `1px solid ${C.border}`,
                            maxWidth: '700px',
                          }}>
                            <div style={{ flex: '0 0 28px', height: '28px', borderRadius: '50%', background: `${C.blue}30`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontSize: '11px', fontWeight: 700, color: C.blue }}>
                                {(u.name || '?').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ color: C.textPrimary, fontSize: '12px', fontWeight: 600 }}>{u.name}</div>
                              <div style={{ color: C.textMuted, fontSize: '11px' }}>{u.email}</div>
                            </div>
                            <div style={{
                              fontSize: '10px', fontWeight: 700, color: C.textMuted,
                              background: C.border, padding: '2px 6px', borderRadius: '4px', letterSpacing: '0.04em',
                            }}>{u.role}</div>
                            <div style={{ color: C.textMuted, fontSize: '11px', whiteSpace: 'nowrap' }}>
                              Joined {formatDate(u.createdAt)}
                            </div>
                            <button
                              onClick={() => setResetUser(u)}
                              title="Reset this user's password"
                              style={{
                                display: 'flex', alignItems: 'center', gap: '5px',
                                padding: '4px 9px', borderRadius: '6px',
                                border: `1px solid ${C.blue}44`, background: `${C.blue}18`,
                                color: C.blue, fontSize: '11px', fontWeight: 600,
                                cursor: 'pointer', whiteSpace: 'nowrap',
                              }}
                            >
                              <KeyRound size={11} /> Reset Password
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {resetUser && (
        <ResetPasswordDialog user={resetUser} onClose={() => setResetUser(null)} />
      )}

      {/* Confirmation dialogs */}
      {confirm?.type === 'expire' && (
        <ConfirmDialog
          title={`Expire "${confirm.tenant.name}"?`}
          message="This will immediately halt the tenant's access. They will see a 'trial expired' message on login. You can extend their trial afterwards if needed."
          confirmLabel="Expire Now"
          confirmColor={C.amber}
          onConfirm={() => expireTenant(confirm.tenant)}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.type === 'purge' && (
        <ConfirmDialog
          title={`Purge "${confirm.tenant.name}" — are you sure?`}
          message="This permanently deletes all operational data (companies, sites, zones, employees, users). Alerts and transactions are retained as an audit trail with PII removed. This cannot be undone."
          confirmLabel="Permanently Purge"
          confirmColor={C.red}
          onConfirm={() => purgeTenant(confirm.tenant)}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
